import React, { useState } from 'react';
import { Typography, Container, Grid, Box, Skeleton, Button, Stack, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useGetCustomerWishlist } from '../../hooks/customer/useGetCustomerWishlist';
import { addToCart } from '../../api/apiStorefront';
import { useQueryClient } from '@tanstack/react-query';
import ProductCard from '../../components/storefront/home/shared/ProductCard';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, ShoppingCartCheckout } from '@mui/icons-material';

// Iterates over the wishlist client-side, calling POST /cart/items per item.
// The backend doesn't expose a bulk endpoint; doing this here keeps the UI
// behaviour consistent and surfaces per-item errors without abandoning the
// rest of the batch.
const moveAllToCart = async (items) => {
  const succeeded = [];
  const failed = [];
  for (const w of items) {
    const product = w?.product || w;
    if (!product?.id) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await addToCart({ product_id: product.id, qty: 1 });
      succeeded.push(product.id);
    } catch (err) {
      failed.push({ id: product.id, message: err?.response?.data?.message });
    }
  }
  return { succeeded, failed };
};

const Wishlist = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: wishlistData, isLoading } = useGetCustomerWishlist();
  const wishlistItems = wishlistData?.data?.items || [];

  const [isMoving, setIsMoving] = useState(false);

  const handleMoveAll = async () => {
    if (isMoving || wishlistItems.length === 0) return;
    setIsMoving(true);
    try {
      const { succeeded, failed } = await moveAllToCart(wishlistItems);
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      queryClient.invalidateQueries({ queryKey: ['cart-count'] });
      if (failed.length === 0) {
        toast.success(
          t('wishlist.movedAll', { defaultValue: 'Moved {{count}} items to cart', count: succeeded.length })
        );
      } else if (succeeded.length === 0) {
        toast.error(t('wishlist.moveAllFailed', 'Could not add any items to cart'));
      } else {
        toast(t('wishlist.movedSome', {
          defaultValue: 'Added {{ok}} of {{total}} — {{failed}} failed',
          ok: succeeded.length,
          failed: failed.length,
          total: succeeded.length + failed.length,
        }), { icon: '⚠️' });
      }
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8, minHeight: '60vh' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 6 }}>
        <Box>
          <Typography variant="h3" fontWeight="900" sx={{ letterSpacing: '-0.02em', mb: 1 }}>
            {t('wishlist.title', 'My Wishlist')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {wishlistItems.length} {t('wishlist.itemsCount', 'items saved')}
          </Typography>
        </Box>
        {wishlistItems.length > 0 && (
          <Button
            variant="contained"
            size="large"
            startIcon={<ShoppingCartCheckout />}
            onClick={handleMoveAll}
            disabled={isMoving}
            sx={{ borderRadius: 3, fontWeight: 700 }}
          >
            {isMoving
              ? t('wishlist.movingAll', 'Moving...')
              : t('wishlist.moveAllToCart', 'Move all to cart')}
          </Button>
        )}
      </Stack>

      {isMoving && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

      {isLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 3, mb: 1 }} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="40%" />
            </Grid>
          ))}
        </Grid>
      ) : wishlistItems.length > 0 ? (
        <Grid container spacing={3}>
          {wishlistItems.map((product) => (
            <Grid item xs={12} sm={6} md={3} key={product.id}>
              <ProductCard
                product={product?.product}
                onClick={() => navigate(`/products/${product?.product?.slug}`)}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{
          textAlign: 'center',
          py: 12,
          px: 4,
          borderRadius: 4,
          border: (theme) => `2px dashed ${theme.palette.divider}`,
          backgroundColor: 'transparent'
        }}>
          <Box sx={{ mb: 3, opacity: 0.3 }}>
            <ShoppingBag sx={{ fontSize: 80 }} />
          </Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t('wishlist.empty', 'Your wishlist is empty')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {t('wishlist.emptyMessage', 'Save items you like to see them here later.')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/')}
            sx={{ borderRadius: 3, px: 4 }}
          >
            {t('wishlist.startShopping', 'Start Shopping')}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default Wishlist;
